import React from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import { useDispatch } from 'react-redux';
import { format } from 'date-fns';
import Button from '@material-ui/core/Button';
import toast from 'utils/toast.jsx';
import CopyToClipboard from 'react-copy-to-clipboard';

import { closeModal } from 'features/utilsSlice';
import useModalOpen from 'hooks/useModalOpen';
import useProfileIds from 'hooks/useProfileIds';
import useIsAdvertiser from 'hooks/useIsAdvertiser';
import DownloadIcon from 'public/icons/download-green-circle.svg';
import CopyIcon from 'public/icons/copy-green-circle.svg';
import IconMainContainer from 'components/IconMainContainer';
import TransitionDialog from 'components/TransitionDialog';
import DisplayAsset from 'components/DisplayAsset';
import styles from 'styles/components/gigs/ReviewDialog.module.scss';

const width = { width: 160 };
const videoSize = { width: 100, height: 100 };

const ReviewAssetDialog = ({ asset, milestone, gig }) => {
  const open = useModalOpen('Review Asset');
  const isAdvertiser = useIsAdvertiser();
  const profileIds = useProfileIds();
  const dispatch = useDispatch();

  const handleCopy = text =>
    toast(
      'success',
      `Successfully copied the ${text}! Make sure to come back to the app, and upload a proof of posting once you're done on ${milestone.name}.`,
      {
        icon: <IconMainContainer src="/icons/notifications/thumb-up.svg" alt="Thumb Up" />,
      }
    );

  const handleClose = () => dispatch(closeModal('Review Asset'));
  const handleClick = () => {
    toast(
      'success',
      `Successfully downloaded the asset! Make sure to come back to the app, and upload a proof of posting once you're done on ${milestone.name}.`,
      {
        icon: <IconMainContainer src="/icons/notifications/thumb-up.svg" alt="Thumb Up" />,
      }
    );
    handleClose();
  };
  const isImage = filename => {
    const extList = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'tif', 'tiff'];
    return extList.some(ext => filename.toLowerCase().includes(`.${ext}`));
  };

  const getDownloadUrl = () => {
    if (!asset?.download_url) return '#';

    const filename = asset.download_url.split('/').pop();

    if (isImage(filename)) {
      return (
        asset.download_url.replace('/proxy/', '/redirect/').split('?')[0] +
        '?disposition=attachment'
      );
    }

    return asset.download_url;
  };

  if (!asset) return null;

  return (
    <TransitionDialog open={open} handleClose={handleClose} styles={styles}>
      <div className={styles.reviewContainer}>
        <div className={styles.assetContainer}>
          <DisplayAsset asset={asset} size={videoSize} whiteIcon />
        </div>

        <div className={styles.date}>
          <span>Added: {format(new Date(milestone.created_at), 'MM/dd/yy, hh:mm a')}</span>
        </div>
        <div className={styles.assetInfo}>
          <div className={styles.row}>
            <div>
              <h3>Asset:</h3>
              <span className={styles.name} style={{ display: 'block' }}>
                {asset.name}
              </span>
            </div>

            <a href={getDownloadUrl()}>
              <DownloadIcon />
            </a>
          </div>

          <div className={styles.row}>
            <div>
              <h3>Size:</h3>
              <span>{asset.file_size}</span>
            </div>
          </div>

          {milestone.caption && (
            <div className={styles.row}>
              <div>
                <h3>Caption:</h3>
                <span style={{ marginRight: 10 }}>{milestone.caption}</span>
              </div>

              <CopyToClipboard text={milestone.caption}>
                <CopyIcon onClick={() => handleCopy(milestone.inventory_space.caption_name)} />
              </CopyToClipboard>
            </div>
          )}

          {milestone.url && (
            <div className={styles.row}>
              <div style={{ minWidth: 200, overflowWrap: 'break-word', marginRight: 10 }}>
                <h3>URL:</h3>
                <span>{milestone.url}</span>
              </div>

              <CopyToClipboard text={milestone.url}>
                <CopyIcon onClick={() => handleCopy('url')} />
              </CopyToClipboard>
            </div>
          )}
        </div>
      </div>

      <div className={styles.buttons}>
        {isAdvertiser || (gig.public && !profileIds.includes(gig.profile_id)) ? (
          <Button onClick={handleClose} variant="contained" color="primary" style={width}>
            Close
          </Button>
        ) : (
          <Link href={getDownloadUrl()} passHref>
            <Button onClick={handleClick} variant="contained" color="primary">
              Download
            </Button>
          </Link>
        )}
      </div>
    </TransitionDialog>
  );
};

ReviewAssetDialog.propTypes = {
  asset: PropTypes.object,
  milestone: PropTypes.object,
};

ReviewAssetDialog.defaultProps = {
  asset: null,
  milestone: null,
};

export default ReviewAssetDialog;
